import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissaoService } from '../../core/services/permissao.service';
import { FORTARIS_GRUPOS_MENU, FORTARIS_MODULOS, FORTARIS_MODULOS_CADASTRO } from '../../core/navigation/fortaris-modulos';
import { InputTextModule } from 'primeng/inputtext';

interface MenuItem {
  label: string;
  icon: string;
  rota?: string;
  permissoes?: string[];
}

interface MenuGrupo {
  titulo: string;
  itens: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, InputTextModule],
  inputs: ['collapsed', 'mobileOpen'],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  @Input() collapsed = false;
  @Input('mobileOpen') mobileOpen = false;
  @Output() itemSelect = new EventEmitter<void>();

  protected readonly grupos: MenuGrupo[] = this.montarGruposMenu();
  protected gruposExpandidos: Record<string, boolean> = {};
  protected termoBusca = '';

  constructor(private readonly permissaoService: PermissaoService, private readonly router: Router) {}

  ngOnInit(): void {
    this.permissaoService.carregarPermissoes();
    this.inicializarEstadoGrupos();
    this.router.events.pipe(filter((evento) => evento instanceof NavigationEnd)).subscribe(() => {
      this.abrirGrupoDaRotaAtual();
    });
  }

  onItemSelect(): void {
    this.itemSelect.emit();
  }

  protected atualizarBusca(): void {
    if (this.termoBusca.trim().length > 0) {
      this.expandirTodosOsGrupos();
      return;
    }
    this.inicializarEstadoGrupos();
  }

  protected podeExibirItem(item: MenuItem): boolean {
    if (!item.permissoes || item.permissoes.length === 0) {
      return true;
    }
    return this.permissaoService.temAlgumaPermissao(item.permissoes);
  }

  protected podeExibirGrupo(grupo: MenuGrupo): boolean {
    return grupo.itens.some((item) => this.podeExibirItem(item));
  }

  protected alternarGrupo(grupo: MenuGrupo): void {
    const chave = grupo.titulo;
    this.gruposExpandidos[chave] = !this.isGrupoExpandido(grupo);
  }

  protected isGrupoExpandido(grupo: MenuGrupo): boolean {
    const chave = grupo.titulo;
    if (!(chave in this.gruposExpandidos)) {
      return true;
    }
    return this.gruposExpandidos[chave];
  }

  protected get gruposFiltrados(): MenuGrupo[] {
    const termo = this.normalizarTexto(this.termoBusca);
    const buscaAtiva = termo.length > 0;

    return this.grupos
      .map((grupo) => {
        const itensPermitidos = grupo.itens.filter((item) => this.podeExibirItem(item));
        const itensFiltrados = buscaAtiva
          ? itensPermitidos.filter((item) =>
              this.contemTermo(`${grupo.titulo} ${item.label}`, termo),
            )
          : itensPermitidos;

        if (itensFiltrados.length === 0) {
          return null;
        }

        return { ...grupo, itens: itensFiltrados };
      })
      .filter((grupo): grupo is MenuGrupo => !!grupo);
  }

  protected get buscaSemResultados(): boolean {
    return this.termoBusca.trim().length > 0 && this.gruposFiltrados.length === 0;
  }

  private montarGruposMenu(): MenuGrupo[] {
    const grupos: MenuGrupo[] = [
      {
        titulo: 'Geral',
        itens: [{ label: 'Início', icon: 'pi pi-home', rota: '/dashboard' }],
      },
    ];

    const modulosMenu = [...FORTARIS_MODULOS, ...FORTARIS_MODULOS_CADASTRO];

    FORTARIS_GRUPOS_MENU.forEach((grupo) => {
      const itens = modulosMenu.filter(
        (modulo) => modulo.grupo === grupo && modulo.visivelMenu,
      ).map((modulo) => ({
        label: modulo.label,
        icon: modulo.icone,
        rota: modulo.rota,
        permissoes:
          modulo.grupo === 'Relatórios'
            ? [
                modulo.permissaoVisualizar,
                'relatorio.visualizar',
                'relatorios.visualizar',
                ...(modulo.modulo === 'relatorios-medicao' ? ['relatorio.execucao.visualizar'] : []),
              ]
            : [modulo.permissaoVisualizar],
      }));

      if (itens.length > 0) {
        grupos.push({ titulo: grupo, itens });
      }
    });

    return grupos;
  }

  private inicializarEstadoGrupos(): void {
    this.gruposExpandidos = this.grupos.reduce<Record<string, boolean>>((acc, grupo) => {
      acc[grupo.titulo] = true;
      return acc;
    }, {});
    this.abrirGrupoDaRotaAtual();
  }

  private abrirGrupoDaRotaAtual(): void {
    const rotaAtual = this.router.url;
    this.grupos.forEach((grupo) => {
      const possuiRota = grupo.itens.some((item) => item.rota && rotaAtual.startsWith(item.rota));
      if (possuiRota) {
        this.gruposExpandidos[grupo.titulo] = true;
      }
    });
  }

  private expandirTodosOsGrupos(): void {
    this.gruposExpandidos = this.grupos.reduce<Record<string, boolean>>((acc, grupo) => {
      acc[grupo.titulo] = true;
      return acc;
    }, {});
  }

  private normalizarTexto(valor: string): string {
    return valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private contemTermo(texto: string, termo: string): boolean {
    return this.normalizarTexto(texto).includes(termo);
  }
}
